import { CountdownTimer } from "@/components/CountdownTimer"
import { Text } from "@/elements/Text"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  BriefcaseIcon as BriefcaseIconOutline,
  ClipboardIcon as ClipboardIconOutline,
  FilmIcon as FilmIconOutline,
  MessageIcon as MessageIconOutline,
  SearchIcon as SearchIconOutline,
  UsersIcon as UsersIconOutline,
} from "@/icons/outline"
import {
  BriefcaseIcon as BriefcaseIconSolid,
  ClipboardIcon as ClipboardIconSolid,
  FilmIcon as FilmIconSolid,
  MessageIcon as MessageIconSolid,
  SearchIcon as SearchIconSolid,
  UsersIcon as UsersIconSolid,
} from "@/icons/solid"
import { NovaLogo, NovaWordmark } from "@/svg"
import type { NovaSession } from "@/types"
import { useApolloClient } from "@apollo/client"
import { ClockIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import Link from "next/link"
import type { JSX } from "react"
import { HelpNavItem } from "./HelpNavItem"
import { NotificationsNavItem } from "./NotificationsNavItem"
import { ProfileNavItem } from "./ProfileNavItem"
import { SettingsNavItem } from "./SettingsNavItem"
import { VerticalNavItem } from "./VerticalNavItem"

import { ampli } from "@/ampli"

type Item = {
  outlineIcon: () => JSX.Element | null
  solidIcon: () => JSX.Element | null
  title: string
  href: string
}

const creativeNavItems = [
  // TODO: Add back when we add dashboard
  // {
  //   outlineIcon: () => <HomeIconOutline className="text-fg-3" />,
  //   solidIcon: () => <HomeIconSolid className="text-fg-1" />,
  //   title: "Home",
  //   href: "/",
  // },
  {
    outlineIcon: () => <BriefcaseIconOutline className="text-fg-3" />,
    solidIcon: () => <BriefcaseIconSolid className="fill-fg-1" />,
    title: "Find work",
    href: "/jobs",
  },
  {
    outlineIcon: () => <ClipboardIconOutline className="text-fg-3" />,
    solidIcon: () => <ClipboardIconSolid className="text-fg-1" />,
    title: "Hire",
    href: "/hire",
  },
  {
    outlineIcon: () => <SearchIconOutline className="text-fg-3" />,
    solidIcon: () => <SearchIconSolid className="text-fg-1" />,
    title: "Discover",
    href: "/discover",
  },
  {
    outlineIcon: () => <FilmIconOutline className="text-fg-3" />,
    solidIcon: () => <FilmIconSolid className="text-fg-1" />,
    title: "Feed",
    href: "/feed",
  },
  {
    outlineIcon: () => <MessageIconOutline className="text-fg-3" />,
    solidIcon: () => <MessageIconSolid className="text-fg-1" />,
    title: "Messages",
    href: "/messages",
  },
  {
    outlineIcon: () => <UsersIconOutline className="text-fg-3" />,
    solidIcon: () => <UsersIconSolid className="text-fg-1" />,
    title: "Network",
    href: "/network",
  },
] as Item[]

const clientNavItems = [
  {
    outlineIcon: () => <ClipboardIconOutline className="text-fg-3" />,
    solidIcon: () => <ClipboardIconSolid className="text-fg-1" />,
    title: "Hire",
    href: "/",
  },
  {
    outlineIcon: () => <SearchIconOutline className="text-fg-3" />,
    solidIcon: () => <SearchIconSolid className="text-fg-1" />,
    title: "Discover",
    href: "/discover",
  },
  {
    outlineIcon: () => <MessageIconOutline className="text-fg-3" />,
    solidIcon: () => <MessageIconSolid className="text-fg-1" />,
    title: "Messages",
    href: "/messages",
  },
] as Item[]

const VerticalNavFragment = graphql(`
  fragment VerticalNav on User {
    id
    uuid
    creative {
      id
      __typename
      ... on IndividualCreative {
        proMembership {
          id
          activeMembershipType
          dayPass {
            id
            expiresAt
          }
        }
      }
    }
    ...NotificationsNavItem
    ...ProfileNavItem
  }
`)

interface Props {
  forceHideLabels?: boolean
  serverSession: NovaSession
  user: FragmentType<typeof VerticalNavFragment> | null | undefined
  size?: "default" | "lg"
  onClickMenuItem?: () => void
}

export const VerticalNav: React.FC<Props> = props => {
  const apolloClient = useApolloClient()

  const { forceHideLabels = false, serverSession, size = "default", onClickMenuItem } = props
  const topItems = serverSession.account === "client" ? clientNavItems : creativeNavItems
  const user = useFragment(VerticalNavFragment, props.user)

  const creative = user?.creative?.__typename === "IndividualCreative" ? user.creative : null
  const hasDayPass = creative?.proMembership?.activeMembershipType === "DAY_PASS"

  const hasSubscription = creative?.proMembership?.activeMembershipType
  const isCreative = props.serverSession.account === "creative"

  const refetchAllQueries = async () => {
    await apolloClient.reFetchObservableQueries()
  }

  const onDayPassExpire = () => {
    setTimeout(refetchAllQueries, 2000)
  }

  return (
    <div className={clsx("flex h-full flex-col justify-between px-8 md:px-0")}>
      <div className="flex flex-col gap-2 px-3">
        <Link href="/">
          <div className="mb-6 hidden justify-center md:flex md:justify-start">
            <div className={clsx("relative ml-[6px] lg:hidden", forceHideLabels && "!flex")}>
              <NovaLogo width={30} height={30} />
              {hasSubscription && isCreative ? (
                <span
                  className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 translate-y-full rounded-[2px] p-1"
                  style={{
                    fontSize: 11,
                    lineHeight: "8px",

                    fontWeight: 500,
                    background: "linear-gradient(134deg, #D1C3FB 0%, #D1B8EF 48.56%, #E8B6C4 100%)",
                  }}>
                  Pro
                </span>
              ) : null}
            </div>
            <div className={clsx("relative hidden lg:flex", forceHideLabels && "!hidden")}>
              <NovaWordmark width={90} height={30} />
              {hasSubscription && isCreative ? (
                <span
                  className="absolute right-[-6.5px] top-0 translate-x-full rounded-[2px] p-1"
                  style={{
                    fontSize: 11,
                    lineHeight: "8px",

                    fontWeight: 500,
                    background: "linear-gradient(134deg, #D1C3FB 0%, #D1B8EF 48.56%, #E8B6C4 100%)",
                  }}>
                  Pro
                </span>
              ) : null}
            </div>
          </div>
        </Link>
        {topItems.map(item => {
          return (
            <div key={item.title} onClick={onClickMenuItem}>
              <VerticalNavItem
                solidIcon={item.solidIcon}
                outlineIcon={item.outlineIcon}
                title={item.title}
                size={size}
                href={item.href}
                forceHideLabel={forceHideLabels}
                accountType={serverSession.account}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-6 flex flex-col gap-4">
        {hasDayPass && (
          <Link
            href="/settings/membership"
            onClick={() => {
              ampli.myMembershipView()
            }}
            className={clsx(!forceHideLabels && "lg:px-3")}>
            <div className="button-pro-gradient-100 flex h-[37px] w-full flex-row items-center justify-center rounded-full border border-primary-3">
              <Text
                variant="CTASmall"
                className={clsx(forceHideLabels && "!hidden", "text-pro-gradient-dark mr-2 hidden lg:block")}>
                Pro
              </Text>
              <CountdownTimer
                variant="CTASmall"
                expiresAt={creative?.proMembership?.dayPass?.expiresAt ?? ""}
                onExpire={onDayPassExpire}
              />
              <ClockIcon
                className={clsx(forceHideLabels && "!hidden", "ml-2 hidden size-[20px] stroke-fg-1 lg:block")}
              />
            </div>
          </Link>
        )}
        <div className="flex flex-col gap-4 px-3">
          <NotificationsNavItem
            forceHideLabel={forceHideLabels}
            user={user}
            size={size}
            serverSession={serverSession}
          />
          <ProfileNavItem forceHideLabel={forceHideLabels} size={size} serverSession={serverSession} user={user} />
          <HelpNavItem />
          <SettingsNavItem
            forceHideLabel={forceHideLabels}
            size={size}
            serverSession={serverSession}
            showProBadge={Boolean(hasSubscription)}
          />
        </div>
      </div>
    </div>
  )
}
